import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Box,
	Container,
	Tabs,
	Tab,
	Card,
	CardContent,
	Button,
	Typography,
	Breadcrumbs,
	Link,
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import { useLazyQuery, useMutation, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { HairServiceInput } from '../../gql/graphql';
import Loader from '../../components/generic/Loader';

// Tab Components
import BasicInfoTab from '../../components/service/BasicInfoTab';
import PricingTab from '../../components/service/PricingTab';
import AnalyticsTab from '../../components/service/AnalyticsTab';
import AdvancedTab from '../../components/service/AdvancedTab';

const GET_SERVICE_BY_ID = gql`
	query hairservice($id: String!) {
		hairservice(_id: $id) {
			_id
			name
			price
			duration_minutes
			description
			taxPercentage
			commissionPercentage
			shopId
			locationId
		}
	}
`;

const ADD_UPDATE_SERVICE = gql`
	mutation AddUpdateHairService($hairService: HairServiceInput!) {
		addUpdateHairService(hairService: $hairService) {
			_id
			name
		}
	}
`;

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`service-tabpanel-${index}`}
			aria-labelledby={`service-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ py: 3 }}>{children}</Box>}
		</div>
	);
}

function ServiceDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState(0);
	const [serviceData, setServiceData] = useState<Partial<HairServiceInput>>({});
	const [isLoading, setIsLoading] = useState(false);

	const isEditMode = !!id && id !== 'new';

	const [getServiceById] = useLazyQuery<Record<'hairservice', HairServiceInput>>(
		GET_SERVICE_BY_ID
	);

	const [addUpdateService, { loading: saving }] = useMutation(ADD_UPDATE_SERVICE, {
		onCompleted: (data) => {
			toast.success(
				`Service: ${data.addUpdateHairService.name} ${isEditMode ? 'updated' : 'created'} successfully.`
			);
			if (!isEditMode) {
				navigate(`/dashboard/services/${data.addUpdateHairService._id}/edit`);
			}
		},
		onError: (error) => {
			toast.error(error.message);
		},
	});

	useEffect(() => {
		if (isEditMode) {
			loadService();
		}
	}, [id]);

	const loadService = async () => {
		setIsLoading(true);
		try {
			const { data } = await getServiceById({
				variables: { id: id! },
			});
			if (data?.hairservice) {
				setServiceData(data.hairservice);
			}
		} catch (error: any) {
			toast.error(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleSave = async () => {
		try {
			// Validate required fields
			if (!serviceData.name || !serviceData.price || !serviceData.duration_minutes) {
				toast.error('Please fill in all required fields (Name, Price, Duration)');
				return;
			}

			await addUpdateService({
				variables: { hairService: serviceData },
			});
		} catch (error: any) {
			toast.error(error?.message);
		}
	};

	const updateServiceData = (updates: Partial<HairServiceInput>) => {
		setServiceData((prev) => ({ ...prev, ...updates }));
	};

	return (
		<>
			<Loader isLoading={isLoading || saving} />
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 4,
				}}
			>
				<Container maxWidth="xl">
					{/* Header */}
					<Box sx={{ mb: 3 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
							<Button
								startIcon={<ArrowBack />}
								onClick={() => navigate('/dashboard/services')}
								sx={{ mr: 2 }}
							>
								Back
							</Button>
							<Box sx={{ flexGrow: 1 }}>
								<Typography variant="h4">
									{isEditMode ? 'Edit Service' : 'Create New Service'}
								</Typography>
								<Breadcrumbs sx={{ mt: 1 }}>
									<Link
										underline="hover"
										color="inherit"
										onClick={() => navigate('/dashboard/services')}
										sx={{ cursor: 'pointer' }}
									>
										Services
									</Link>
									<Typography color="text.primary">
										{isEditMode ? serviceData.name || 'Edit' : 'New Service'}
									</Typography>
								</Breadcrumbs>
							</Box>
							<Button
								variant="contained"
								startIcon={<Save />}
								onClick={handleSave}
								disabled={saving}
							>
								Save Service
							</Button>
						</Box>
					</Box>

					{/* Tabs */}
					<Card>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<Tabs
								value={currentTab}
								onChange={handleTabChange}
								variant="scrollable"
								scrollButtons="auto"
							>
								<Tab label="Basic Information" />
								<Tab label="Pricing & Settings" />
								<Tab label="Analytics" disabled={!isEditMode} />
								<Tab label="Advanced" />
							</Tabs>
						</Box>

						<CardContent>
							<TabPanel value={currentTab} index={0}>
								<BasicInfoTab
									serviceData={serviceData}
									updateServiceData={updateServiceData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={1}>
								<PricingTab
									serviceData={serviceData}
									updateServiceData={updateServiceData}
								/>
							</TabPanel>

							<TabPanel value={currentTab} index={2}>
								{isEditMode && serviceData._id && (
									<AnalyticsTab serviceId={serviceData._id} />
								)}
							</TabPanel>

							<TabPanel value={currentTab} index={3}>
								<AdvancedTab
									serviceData={serviceData}
									updateServiceData={updateServiceData}
								/>
							</TabPanel>
						</CardContent>
					</Card>
				</Container>
			</Box>
		</>
	);
}

export default ServiceDetailPage;
